import React, { useState, useEffect } from 'react';
import { PlusCircle, Edit, Trash2, Save, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../../../components/Layout';
import siteContentData from '../../../siteContentData.json';

interface SiteSection {
  _id?: string;
  id: string;
  title: string;
  description: string;
  course?: string;
  modules?: string[];
  order?: number;
  createdAt?: string;
  updatedAt?: string;
  __v?: number;
}

const AdminSiteSections: React.FC = () => {
  const [sections, setSections] = useState<SiteSection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editingSection, setEditingSection] = useState<SiteSection | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    loadSections();
  }, []);

  const loadSections = async () => {
    try {
      setIsLoading(true);
      // Simuler un chargement depuis l'API
      // Dans une vraie application, vous feriez une requête API ici
      // const response = await api.get('/admin/content/sections');
      // setSections(response.data.sections || []);
      
      // Chargement depuis le fichier JSON local
      setSections(siteContentData.sections || []);
    } catch (error) {
      console.error('Error loading sections:', error);
      toast.error('Failed to load sections.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditingSection(prev => ({
      ...prev!,
      [name]: value
    }));
  };

  const openCreateModal = () => {
    setEditingSection({
      id: `section-${Date.now()}`,
      title: '',
      description: '',
      order: sections.length + 1
    });
    setIsModalOpen(true);
  };

  const openEditModal = (section: SiteSection) => {
    setEditingSection({ ...section });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingSection(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingSection) return;

    setIsSaving(true);
    try {
      // Simuler une requête API
      // Dans une vraie application, vous feriez une requête API ici
      // if (editingSection._id) {
      //   await api.put(`/admin/content/sections/${editingSection._id}`, editingSection);
      //   toast.success('Section updated successfully!');
      // } else {
      //   await api.post('/admin/content/sections', editingSection);
      //   toast.success('Section created successfully!');
      // }
      
      // Simulation de sauvegarde locale
      if (editingSection._id) {
        // Mise à jour
        setSections(prev => prev.map(s => 
          s._id === editingSection._id ? editingSection : s
        ));
        toast.success('Section updated successfully!');
      } else {
        // Création
        const newSection = {
          ...editingSection,
          _id: `new-${Date.now()}`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        setSections(prev => [...prev, newSection]);
        toast.success('Section created successfully!');
      }
      
      closeModal();
    } catch (error) {
      console.error('Error saving section:', error);
      toast.error('Failed to save section.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this section?')) {
      try {
        // Simuler une suppression API
        // await api.delete(`/admin/content/sections/${id}`);
        
        // Suppression locale
        setSections(prev => prev.filter(s => s._id !== id));
        toast.success('Section deleted successfully!');
      } catch (error) {
        console.error('Error deleting section:', error);
        toast.error('Failed to delete section.');
      }
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FFB639]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Manage Site Sections</h1>
          <button
            onClick={openCreateModal}
            className="flex items-center px-4 py-2 bg-[#FFB639] text-white rounded-lg hover:bg-[#e5a333] transition-colors"
          >
            <PlusCircle className="h-4 w-4 mr-2" />
            Add Section
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {sections.length === 0 ? (
            <p className="text-gray-500 text-center">No sections found. Add one!</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sections.map((section) => (
                    <tr key={section._id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{section.order}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{section.id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{section.title}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 truncate max-w-xs">{section.description}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => openEditModal(section)}
                          className="text-[#FFB639] hover:text-black mr-4"
                          title="Edit"
                        >
                          <Edit className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(section._id!)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Edit/Create Modal */}
      {isModalOpen && editingSection && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-2xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingSection._id ? 'Edit Section' : 'Add New Section'}
              </h2>
              <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Order</label>
                <input
                  type="number"
                  name="order"
                  value={editingSection.order || ''}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#FFB639] focus:border-[#FFB639]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">ID (unique identifier)</label>
                <input
                  type="text"
                  name="id"
                  value={editingSection.id}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#FFB639] focus:border-[#FFB639]"
                  required
                  disabled={!!editingSection._id} // Disable editing ID for existing sections
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Title</label>
                <input
                  type="text"
                  name="title"
                  value={editingSection.title}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#FFB639] focus:border-[#FFB639]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  name="description"
                  value={editingSection.description || ''}
                  onChange={handleInputChange}
                  rows={4}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#FFB639] focus:border-[#FFB639]"
                />
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FFB639]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-[#FFB639] hover:bg-[#e5a333] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FFB639] disabled:opacity-50"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AdminSiteSections;